import React from 'react';
import { useTranslation } from 'react-i18next';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { methodKeys, getLocalizedMethodLabel } from '../../../utils/verificationLabels';
import type { MethodRank } from './types';

interface VerificationMethodPickerProps {
  rankOf: (key: string) => MethodRank;
  onChange: (key: string, rank: MethodRank) => void;
}

const RANKS: MethodRank[] = ['none', 'primary', 'secondary'];
const RANK_SYMBOLS: Record<MethodRank, string> = {
  none: '-',
  primary: '■',
  secondary: '□',
};

const VerificationMethodPicker: React.FC<VerificationMethodPickerProps> = ({
  rankOf,
  onChange,
}) => {
  const { t } = useTranslation();

  return (
    <div>
      <h6 className="revy-cjk mb-2 text-sm font-medium leading-[1.5]">
        {t('verification.table.verificationMethod')} (■{t('verification.primary')} / □
        {t('verification.secondary')})
      </h6>
      <div className="flex flex-wrap gap-3">
        {methodKeys.map((key) => (
          <div key={key} className="flex min-w-20 flex-col items-center">
            <span id={`method-${key}`} className="revy-cjk mb-0.5 text-[0.7rem] leading-[1.5]">
              {getLocalizedMethodLabel(t, key)}
            </span>
            <ToggleGroup
              type="single"
              // Radix gives a single-type group role="radiogroup"; without this
              // all eight announce as an unnamed group.
              aria-labelledby={`method-${key}`}
              variant="outline"
              size="sm"
              value={rankOf(key)}
              // Radix reports '' when the pressed item is the active one; the
              // rank must stay set, exactly as the exclusive MUI group behaved.
              onValueChange={(value) => {
                if (value) onChange(key, value as MethodRank);
              }}
              className="gap-0"
            >
              {RANKS.map((rank) => (
                <ToggleGroupItem
                  key={rank}
                  value={rank}
                  className="h-7 min-w-7 rounded-none px-2 text-[0.65rem] leading-[1.5] first:rounded-l-md last:rounded-r-md [&+*]:border-l-0"
                >
                  {RANK_SYMBOLS[rank]}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VerificationMethodPicker;
